import type { ComponentType } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { audio } from '../../audio'
import { uiArt } from '../matchmaking/art'
import styles from './podium.module.css'
import { COUNT_UP_MS, valueAt } from './rewardRows'
import type { RewardRow, RewardRows } from './rewardRows'

const MEDAL_KINDS = ['ui_medal_gold', 'ui_medal_silver', 'ui_medal_bronze'] as const
const MEDAL_NAMES = ['Gold medal', 'Silver medal', 'Bronze medal'] as const
/**
 * The count-up redraws this often (a timer, not rAF: it still finishes in a hidden tab); each
 * step is one `reward_tick` (T3.6).
 */
const STEP_MS = 50

/**
 * Milliseconds since the panel mounted, until `durationMs` (then it stops ticking). `onStep`
 * runs once per count-up step.
 */
function useElapsed(durationMs: number, onStep: () => void): number {
  const [ms, setMs] = useState(0)
  const step = useRef(onStep)
  step.current = onStep
  useEffect(() => {
    const start = Date.now()
    const id = setInterval(() => {
      const t = Date.now() - start
      setMs(Math.min(t, durationMs))
      step.current()
      if (t >= durationMs) clearInterval(id)
    }, STEP_MS)
    return () => clearInterval(id)
  }, [durationMs])
  return ms
}

function Icon({ src, alt, fallback }: { src: string | null; alt: string; fallback: string }) {
  return src ? (
    <img class={styles.icon} src={src} alt={alt} />
  ) : (
    <span class={styles.dot} style={{ background: fallback }} role="img" aria-label={alt} />
  )
}

function Detail({ row }: { row: RewardRow }) {
  if (row.key === 'shards') {
    return (
      <span class={styles.detail}>
        {row.detail}
        <Icon src={uiArt('coin')} alt="shard" fallback="#ffd43b" />
      </span>
    )
  }
  if (row.medal !== null) {
    return (
      <span class={styles.detail}>
        <Icon
          src={uiArt(MEDAL_KINDS[row.medal - 1]!)}
          alt={MEDAL_NAMES[row.medal - 1]!}
          fallback={['#ffd43b', '#d7dde8', '#d08a4f'][row.medal - 1]!}
        />
      </span>
    )
  }
  return (
    <span class={`${styles.detail} ${row.value === null ? styles.soon : ''}`}>{row.detail}</span>
  )
}

/**
 * The Match result rows (PHASE3 T3.5): shards → coins, the place's coins, League (coming with
 * T3.14) and XP, counting up from 0 over 600 ms; then Phase 2's reward lines (+XP, level-up,
 * "(not saved)", tasks) through the `rewardLines` slot when the session has it (D-053).
 */
export function RewardPanel({ reward, Slot }: { reward: RewardRows | null; Slot?: ComponentType }) {
  // A tick per count-up step, only while some row has something to count.
  const counting = reward?.rows.some((row) => (row.value ?? 0) > 0) ?? false
  const ms = useElapsed(COUNT_UP_MS, () => {
    if (counting) audio.play('reward_tick')
  })
  return (
    <div class={styles.reward}>
      {reward && (
        <ul class={styles.rows} data-testid="reward-rows">
          {reward.rows.map((row) => (
            <li key={row.key} class={`${styles.row} ${row.value === null ? styles.dim : ''}`}>
              <span class={styles.label}>{row.label}</span>
              <Detail row={row} />
              <span class={styles.value}>
                {row.value === null ? '' : `+${valueAt(ms, row.value)} ${row.unit ?? ''}`}
              </span>
            </li>
          ))}
        </ul>
      )}
      {reward?.note && <p class={styles.note}>{reward.note}</p>}
      {Slot && <Slot />}
    </div>
  )
}
